import { API_URL, DOCKER_KEY, DOCKER_RUN_API_URL } from "env"

const AES = require('crypto-js/aes');
const Base64 = require('crypto-js/enc-base64');
const Hex = require('crypto-js/enc-hex');
const HmacSHA256 = require('crypto-js/hmac-sha256');

type RunPayload = {
    account: string;
    password: string;
    platform: 'mt4' | 'mt5' | 'ctrader';
    server?: string;
    ctrader_id?: string;
}

export type TradeAccountCredentials = {
    account: string;
    password: string;
    server: string;
    platform: string;
    ctraderId: string;
}

export type DockerContainerStatusResponse = {
    platform?: string;
    account?: string;
    container?: {
        account?: string;
        platform?: string;
        server?: string;
        container_name?: string;
        [key: string]: any;
    };
    status?: {
        State?: string;
        Status?: string;
        [key: string]: any;
    };
    [key: string]: any;
}

type DockerLogDetails = {
    container?: string;
    directory?: string;
    file_path?: string;
    content?: string;
    [key: string]: any;
}

export type DockerContainerLogsResponse = {
    platform?: string;
    account?: string;
    tail?: number;
    logs?: DockerLogDetails | Record<string, DockerLogDetails | undefined>;
}

type DockerRequestMethod = 'GET' | 'POST';

async function fetchDockerEndpoint<T>(
    path: string,
    method: DockerRequestMethod = 'GET',
    body?: object,
): Promise<T | null> {
    const endpoint = `${getDockerApiBaseUrl()}${path}`;
    const res = await fetch(endpoint, {
        method,
        headers: {
            'Content-Type': 'application/json',
            'X-API-Key': 'QH3C2JbUzkYt1sfz96VTn_slED9V81-V9wy1eUUU1-M'
        },
        body: method === 'POST' ? JSON.stringify(body) : undefined
    });

    if (res.status === 404) {
        return null;
    }

    if (!res.ok) {
        throw new Error(`API request failed with status ${res.status}`);
    }

    return await res.json();
}

function encodeBase64Url(value: string): string {
    const bytes = new TextEncoder().encode(value);
    return encodeBytesToBase64Url(bytes);
}

function encodeBytesToBase64Url(bytes: Uint8Array): string {
    let binary = '';

    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });

    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/g, '');
}

function buildFernetKey(keyValue: string): string {
    const padded = keyValue.padEnd(32, '0').slice(0, 32);
    return encodeBase64Url(padded);
}

function normalizeFernetSecret(sharedKey: string): string {
    return buildFernetKey(sharedKey)
        .replace(/-/g, '+')
        .replace(/_/g, '/');
}

function decodeBase64ToHex(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const binary = atob(padded);
    let hex = '';

    for (let index = 0; index < binary.length; index += 1) {
        hex += binary.charCodeAt(index).toString(16).padStart(2, '0');
    }

    return hex;
}

function createFernetKeys(sharedKey: string) {
    const secretHex = decodeBase64ToHex(normalizeFernetSecret(sharedKey));

    if (secretHex.length !== 64) {
        throw new Error('Secret must be 32 url-safe base64-encoded bytes.');
    }

    return {
        signingKey: Hex.parse(secretHex.slice(0, 32)),
        encryptionKey: Hex.parse(secretHex.slice(32))
    };
}

function createRandomHex(byteLength: number): string {
    if (!globalThis.crypto?.getRandomValues) {
        throw new Error('Browser crypto.getRandomValues is unavailable.');
    }

    const bytes = globalThis.crypto.getRandomValues(new Uint8Array(byteLength));
    let hex = '';

    bytes.forEach((byte) => {
        hex += byte.toString(16).padStart(2, '0');
    });

    return hex;
}

function createTimeWordArray() {
    const seconds = Math.round(Date.now() / 1000);
    return Hex.parse(seconds.toString(16).padStart(16, '0'));
}

function createFernetToken(message: string, sharedKey: string): string {
    const { signingKey, encryptionKey } = createFernetKeys(sharedKey);
    const version = Hex.parse('80');
    const time = createTimeWordArray();
    const iv = Hex.parse(createRandomHex(16));
    const cipherText = AES.encrypt(message, encryptionKey, { iv }).ciphertext;

    let signingPayload = version.concat(time);
    signingPayload = signingPayload.concat(iv);
    signingPayload = signingPayload.concat(cipherText);

    const hmac = HmacSHA256(signingPayload, signingKey);
    const token = signingPayload.concat(hmac).toString(Base64);

    return token.replace(/\+/g, '-').replace(/\//g, '_');
}

function getDockerRunSharedKey(): string {
    return DOCKER_KEY;
}

function getDockerApiBaseUrl(): string {
    return new URL(DOCKER_RUN_API_URL).origin;
}

export async function encryptRunData(data: object, sharedKey: string): Promise<string> {
    return createFernetToken(JSON.stringify(data), sharedKey);
}

function normalizeRunPlatform(platform: string): RunPayload['platform'] {
    const normalized = platform.trim().toLowerCase();

    if (normalized === 'mt4' || normalized === 'mt5' || normalized === 'ctrader') {
        return normalized;
    }

    throw new Error(`Unsupported trade platform: ${platform}`);
}

const createDefaultCredentials = (account: string, platform: string): TradeAccountCredentials => ({
    account,
    password: '',
    server: '',
    platform,
    ctraderId: ''
});

const normalizeCredentialsResponse = (
    source: any,
    account: string,
    platform: string
): TradeAccountCredentials => {
    const fallback = createDefaultCredentials(account, platform);

    if (!source) {
        return fallback;
    }

    return {
        account: String(
            source.account
            ?? source.account_number
            ?? source.accountNumber
            ?? source.accountId
            ?? fallback.account
        ),
        password: String(
            source.password
            ?? source.master_password
            ?? source.masterPassword
            ?? source.read_only_password
            ?? source.readOnlyPassword
            ?? ''
        ),
        server: String(
            source.server
            ?? source.server_name
            ?? source.serverName
            ?? source.broker
            ?? ''
        ),
        platform: String(
            source.platform
            ?? source.platform_code
            ?? source.platformCode
            ?? fallback.platform
        ),
        ctraderId: String(
            source.ctrader_id
            ?? source.ctraderId
            ?? source.cTraderId
            ?? source.ctid
            ?? ''
        )
    };
};

/**
 * Fetches account information for the provided trade account number from the backend API.
 *
 * Performs a GET request to `${API_URL}/api/v1/be/trade_accounts?account_number=${account_number}`.
 * The request includes a JSON content-type and an Authorization header using a bearer token read
 * from `localStorage.getItem('token')`. On success, the function returns the first element of
 * the returned items array (`data.data.items[0]`).
 *
 * @param account_number - The trade account number to look up.
 * @returns A promise that resolves to the account information object (type: any). If the API
 *          response contains no items, the resolved value may be `undefined`.
 * @throws Will throw an Error if the HTTP response status is not OK, or if a network/JSON parsing
 *         error occurs during the fetch.
 * @async
 * @example
 * // Usage
 * const accountInfo = await getInfoAccount('ACC123456');
 */
async function getInfoAccount(account_number: string): Promise<any> {
    try {
        const res = await fetch(`${API_URL}/api/v1/be/trade_accounts?account_number=${account_number}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });

        if (res.ok) {
            const data = await res.json();
            return data.data.items[0];
        } else {
            throw new Error(`API request failed with status ${res.status}`);
        }
    }
    catch (error) {
        console.error('Error fetching account info:', error);
        throw error;
    }
}

/**
 * Fetches a trade-history report for a given account and date range.
 *
 * Builds a query to /api/v1/be/trade-history/reports including the required
 * account_number and date_to parameters and conditionally adding date_from when
 * a non-empty `from` value is provided.
 *
 * The request is sent as a GET to `${API_URL}${apiUrl}` with
 * `Content-Type: application/json` and an `Authorization: Bearer <token>`
 * header where the token is read from `localStorage.getItem('token')`.
 *
 * @param id - Account number for which to fetch the report.
 * @param from - Optional start date for the report (include only when non-empty).
 *               Expected to be a date string accepted by the backend (e.g. ISO 8601).
 * @param to - End date for the report. Expected to be a date string accepted by the backend.
 *
 * @returns A Promise that resolves with the parsed JSON response from the API.
 *
 * @throws Will throw an Error if the HTTP response has a non-OK status,
 *         or will propagate network/fetch errors. Errors are also logged to the console.
 *
 * @remarks
 * - Side effects: reads `localStorage` for the auth token and performs a network request via `fetch`.
 * - Consider narrowing the return type from `Promise<any>` to a more specific interface representing the report shape.
 *
 * @example
 * const report = await getReport('12345', '2024-01-01', '2024-01-31');
 */
async function getReport(id: string, from: string, to: string): Promise<any> {
    // Build API URL with conditional date_from parameter
    let apiUrl = `/api/v1/be/trade-history/reports?account_number=${id}&date_to=${to}`
    if (from && from !== '') {
        apiUrl += `&date_from=${from}`
    }

    try {
        // Make the API call
        const res = await fetch(`${API_URL}${apiUrl}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });

        // Parse and return the JSON response
        if (res.ok) {
            const data = await res.json();
            return data;
        } else {
            throw new Error(`API request failed with status ${res.status}`);
        }
    }
    catch (error) {
        console.error('Error fetching report:', error);
        throw error;
    }
}

async function getImportReport(id: string, from: string, to: string): Promise<any> {
    // Build API URL with conditional date_from parameter
    let apiUrl = `/api/v1/be/trade-history-import/reports?account_number=${id}`
    if (to && to !== '') {
        apiUrl += `&date_to=${to}`
    }
    if (from && from !== '') {
        apiUrl += `&date_from=${from}`
    }

    try {
        // Make the API call
        const res = await fetch(`${API_URL}${apiUrl}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });

        // Parse and return the JSON response
        if (res.ok) {
            const data = await res.json();
            return data;
        } else {
            throw new Error(`API request failed with status ${res.status}`);
        }
    }
    catch (error) {
        console.error('Error fetching report:', error);
        throw error;
    }
}

async function getLiveTrades(account_number: string, limit = 10, page = 1): Promise<any> {
    try {
        const res = await fetch(`${API_URL}/api/v1/live-trading?account_number=${account_number}&limit=${limit}&page=${page}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });

        if (res.ok) {
            const data = await res.json();
            return data.data;
        } else {
            throw new Error(`API request failed with status ${res.status}`);
        }
    }
    catch (error) {
        console.error('Error fetching live trades:', error);
        throw error;
    }
}

async function saveTradeAccountCredentials(
    account_number: string,
    payload: TradeAccountCredentials
): Promise<any> {
    try {
        const platform = normalizeRunPlatform(payload.platform);
        const runPayload: RunPayload = {
            account: payload.account || account_number,
            password: payload.password,
            platform
        };

        if (platform !== 'ctrader' && payload.server) {
            runPayload.server = payload.server;
        }

        if (platform === 'ctrader' && payload.ctraderId) {
            runPayload.ctrader_id = payload.ctraderId;
        }

        const sharedKey = getDockerRunSharedKey();
        const encryptedPayload = await encryptRunData(runPayload, sharedKey);
        const requestBody = {
            data: encryptedPayload
        };
        const res = await fetch(DOCKER_RUN_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': 'QH3C2JbUzkYt1sfz96VTn_slED9V81-V9wy1eUUU1-M'
            },
            body: JSON.stringify(requestBody)
        });

        if (!res.ok) {
            throw new Error(`API request failed with status ${res.status}`);
        }

        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
            return await res.json();
        }

        return null;
    }
    catch (error) {
        console.error('Error saving trade account credentials:', error);
        throw error;
    }
}

async function getDockerContainerStatus(
    platform: string,
    account_number: string
): Promise<DockerContainerStatusResponse | null> {
    try {
        const normalizedPlatform = normalizeRunPlatform(platform);
        return await fetchDockerEndpoint<DockerContainerStatusResponse>(`/api/docker/status/${normalizedPlatform}/${account_number}`);
    }
    catch (error) {
        console.error('Error fetching docker container status:', error);
        throw error;
    }
}

async function getDockerContainerLogs(
    platform: string,
    account_number: string,
    tail = 10
): Promise<DockerContainerLogsResponse | null> {
    try {
        const normalizedPlatform = normalizeRunPlatform(platform);
        return await fetchDockerEndpoint<DockerContainerLogsResponse>(`/api/docker/logs/${normalizedPlatform}/${account_number}?tail=${tail}`);
    }
    catch (error) {
        console.error('Error fetching docker container logs:', error);
        throw error;
    }
}

// async function restartDockerContainer(
//     platform: string,
//     account_number: string
// ): Promise<any> {
//     try {
//         const normalizedPlatform = normalizeRunPlatform(platform);
//         return await fetchDockerEndpoint<any>(`/api/docker/restart/${normalizedPlatform}/${account_number}`, 'POST');
//     }
//     catch (error) {
//         console.error('Error restarting docker container:', error);
//         throw error;
//     }
// }

/**
 * Tắt / Dừng Docker Container
 */
async function stopDockerContainer(
    container_id: string,
): Promise<any> {
    try {
        return await fetchDockerEndpoint<any>(
            `/api/docker/stop`,
            'POST',
            {
                id: container_id
            }
        );
    } catch (error) {
        console.error('Error stopping docker container:', error);
        throw error;
    }
}

/**
 * Xóa Docker Container
 */
async function deleteDockerContainer(
    container_id: string,
    account_number?: string,
    platform?: string
): Promise<any> {
    try {
        await fetchDockerEndpoint<any>(
            `/api/db/remove`,
            'POST',
            {
                account: account_number,
                platform
            }
        );
        return await fetchDockerEndpoint<any>(
            `/api/docker/remove`,
            'POST',
            {
                container_id: container_id
            }
        );
    } catch (error) {
        console.error('Error deleting docker container:', error);
        throw error;
    }
}

async function restartDockerContainer(
    container_name: string,
): Promise<any> {
    try {
        return await fetchDockerEndpoint<any>(
            `/api/docker/restart`,
            'POST',
            {
                id: container_name
            }
        );
    } catch (error) {
        console.error('Error restarting docker container:', error);
        throw error;
    }
}

export {
    getInfoAccount,
    getReport,
    getImportReport,
    getLiveTrades,
    saveTradeAccountCredentials,
    getDockerContainerStatus,
    getDockerContainerLogs,
    restartDockerContainer,
    stopDockerContainer,
    deleteDockerContainer
}